import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  ClockIcon,
  CalendarIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  ComputerDesktopIcon,
  SpeakerWaveIcon,
  SquaresPlusIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  CloudArrowUpIcon,
  BellIcon,
  ArrowUpRightIcon,
  MapPinIcon,
  NewspaperIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useAnnouncementCount } from '../../hooks/useAnnouncements';
import { toast } from 'react-toastify';
import UserProfileModal from '../common/UserProfileModal';
import PasswordResetModal from '../common/PasswordResetModal';
import AnnouncementModal from '../common/AnnouncementModal';
import DateTime from '../common/DateTime';
import { Skeleton } from '../common/Components';

const EXTERNAL_LINKS = [
  // { name: 'Recruit', href: 'https://chat.openai.com', title: 'QJ Recruit', className: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white' },
  { name: 'QJ BA', href: 'https://qj-ba-internal-frontend-626820507751.us-central1.run.app/login', title: 'QJ Business Assistant', className: 'bg-amber-50 text-amber-800 hover:bg-amber-500 hover:text-white' },
];

const PageFallback = () => (
  <div className="space-y-3" role="status" aria-label="Loading page">
    <Skeleton className="h-8 w-56" />
    <Skeleton className="h-4 w-80 max-w-full" />
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
    </div>
    <Skeleton className="h-64" />
  </div>
);

const NavLink = ({ item, onNavigate }) => (
  <Link
    to={item.href}
    onClick={onNavigate}
    aria-current={item.current ? 'page' : undefined}
    className={`group relative flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${item.current
      ? 'bg-blue-50 text-blue-700'
      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
  >
    {item.current && <span className="absolute left-0 top-1 bottom-1 w-1 rounded-r-full bg-blue-600" aria-hidden="true" />}
    <item.icon className={`h-5 w-5 flex-shrink-0 ${item.current ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'}`} aria-hidden="true" />
    <span className="truncate">{item.name}</span>
  </Link>
);

const SidebarContent = ({ sections, onNavigate }) => (
  <nav className="flex-1 space-y-3 px-2 py-2.5" aria-label="Main">
    {sections.map(section => (
      <div key={section.title}>
        <p className="px-2.5 mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-green-700">{section.title}</p>
        <div className="space-y-0.5">
          {section.items.map(item => <NavLink key={item.href} item={item} onNavigate={onNavigate} />)}
        </div>
      </div>
    ))}
  </nav>
);

const Brand = () => (
  <Link to="/dashboard" className="flex items-center min-w-0" aria-label="QJ HRMS home">
    <span className="text-sm font-semibold text-gray-900 truncate">
      QJ <span className="text-orange-600">HRMS</span>
    </span>
  </Link>
);

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const { user, logout, isAdmin, isHR, showPasswordResetModal, hidePasswordResetModal, handlePasswordResetSuccess, hasAssetAccess } = useAuth();
  const { count: announcementCount } = useAnnouncementCount();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  // Close the drawer on navigation and with Escape
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  const sections = useMemo(() => {
    const isCurrent = (href) => location.pathname === href || location.pathname.startsWith(`${href}/`);
    const withState = (items) => items.map(item => ({ ...item, current: isCurrent(item.href) }));
    return [
      {
        title: 'Overview',
        items: withState([
          { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
          ...(isAdmin || isHR ? [{ name: 'Employees', href: '/employees', icon: UserGroupIcon }] : []),
        ])
      },
      {
        title: 'My Work',
        items: withState([
          { name: 'Attendance', href: '/attendance', icon: ClockIcon },
          { name: 'Leave', href: '/leaves', icon: CalendarIcon },
          { name: 'Work From Home', href: '/wfh', icon: BuildingOfficeIcon },
          { name: 'On-Site Requests', href: '/onsite-requests', icon: MapPinIcon },
          { name: 'Timesheets', href: '/timesheets', icon: DocumentTextIcon },
          { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentListIcon },
          { name: 'Holidays', href: '/holidays', icon: CalendarDaysIcon },
        ])
      },
      {
        title: 'Team',
        items: withState([
          { name: 'Team Flow', href: '/managers', icon: UsersIcon },
          { name: 'Project Allocation', href: '/project-allocation', icon: BriefcaseIcon },
          { name: 'Cloud Storage', href: '/share-drive', icon: CloudArrowUpIcon },
        ])
      },
      ...((hasAssetAccess || isAdmin || isHR) ? [{
        title: 'Company',
        items: withState([
          ...(hasAssetAccess ? [
            { name: 'Assets', href: '/assets', icon: ComputerDesktopIcon },
            { name: 'Blogs', href: '/blogs', icon: NewspaperIcon },
          ] : []),
          ...(isAdmin || isHR ? [
            { name: 'Masters', href: '/masters', icon: SquaresPlusIcon },
            { name: 'Announcements', href: '/announcements', icon: SpeakerWaveIcon },
            { name: 'Reports', href: '/reports', icon: ChartBarIcon },
          ] : []),
        ])
      }] : []),
    ];
  }, [location.pathname, isAdmin, isHR, hasAssetAccess]);

  // Bottom bar on phones: the four most used sections + the full menu
  const bottomNav = useMemo(() => {
    const all = sections.flatMap(s => s.items);
    return ['/dashboard', '/attendance', '/leaves', '/wfh'].map(href => all.find(i => i.href === href)).filter(Boolean);
  }, [sections]);

  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase() || 'U';
  const badgeCount = announcementCount > 9 ? '9+' : announcementCount;

  return (
    <div className="min-h-screen bg-gray-50">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-white focus:px-3 focus:py-2 focus:shadow">
        Skip to content
      </a>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-40 md:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`} aria-hidden={!sidebarOpen}>
        <div
          className={`absolute inset-0 bg-gray-900/50 transition-opacity duration-200 ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setSidebarOpen(false)}
        />
        <aside
          className={`relative flex h-full w-72 max-w-[85vw] flex-col bg-white shadow-xl transition-transform duration-200 ease-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
          aria-label="Navigation menu"
        >
          <div className="flex h-12 items-center justify-between border-b border-gray-200 px-3 safe-area-top">
            <Brand />
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
              aria-label="Close menu"
              tabIndex={sidebarOpen ? 0 : -1}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <SidebarContent sections={sections} onNavigate={() => setSidebarOpen(false)} />
          </div>
          <div className="border-t border-gray-200 p-2 space-y-1.5 safe-area-bottom">
            <div className="flex gap-2">
              {EXTERNAL_LINKS.map(link => (
                <a key={link.name} href={link.href} target="_blank" rel="noopener noreferrer" title={link.title}
                  className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors ${link.className}`}>
                  {link.name}<ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              ))}
            </div>
            <button type="button" onClick={handleLogout}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-100 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-white hover:bg-red-300">
              <ArrowRightOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
              Logout
            </button>
          </div>
        </aside>
      </div>

      {/* Static sidebar for desktop */}
      <aside className="hidden md:fixed md:inset-y-0 md:flex md:w-56 md:flex-col border-r border-gray-200 bg-white">
        <div className="flex h-12 items-center border-b border-gray-200 px-4">
          <Brand />
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <SidebarContent sections={sections} />
        </div>
      </aside>

      {/* Main content */}
      <div className="flex min-h-screen flex-col md:pl-56">
        {/* Top navigation bar */}
        <header className="sticky top-0 z-30 flex h-12 flex-shrink-0 items-center gap-2 border-b border-gray-200 bg-white/95 backdrop-blur px-2 sm:px-3 safe-area-top">
          <button
            type="button"
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            aria-expanded={sidebarOpen}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <button
            type="button"
            onClick={() => setShowUserProfileModal(true)}
            className="group flex min-w-0 items-center gap-2 rounded-lg px-1.5 py-0.5 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            title="Edit profile"
          >
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white ring-2 ring-blue-100">
              {initials}
            </span>
            <span className="min-w-0 text-left">
              <span className="block truncate text-sm font-semibold leading-tight text-gray-800">{user?.first_name} {user?.last_name}</span>
              <span className="block truncate text-[11px] md:text-xs leading-tight text-gray-500 capitalize">{user?.designation?.toLowerCase()}</span>
            </span>
          </button>

          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            <div className="hidden lg:block"><DateTime /></div>
            <div className="hidden md:flex items-center gap-1.5 border-l border-gray-200 pl-2">
              {EXTERNAL_LINKS.map(link => (
                <a key={link.name} href={link.href} target="_blank" rel="noopener noreferrer" title={link.title}
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium transition-colors ${link.className}`}>
                  {link.name}<ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" />
                </a>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setShowAnnouncementModal(true)}
              className="relative rounded-lg p-1.5 text-gray-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label={`Announcements${announcementCount ? ` (${announcementCount} new)` : ''}`}
            >
              <BellIcon className="h-5 w-5" />
              {announcementCount > 0 && (
                <span className="absolute right-0.5 top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
                  {badgeCount}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="hidden md:inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-red-600 hover:text-white bg-red-100 hover:bg-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              title="Logout"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
              <span className="hidden lg:inline">Logout</span>
            </button>
          </div>
        </header>

        <main id="main-content" className="flex-1 pb-16 md:pb-0" tabIndex={-1}>
          <div key={location.pathname} className="mx-auto w-full max-w-screen-2xl px-2.5 py-3 sm:px-4 sm:py-4 animate-fade-in">
            {children || (
              <Suspense fallback={<PageFallback />}>
                <Outlet />
              </Suspense>
            )}
          </div>
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-gray-200 bg-white/95 backdrop-blur md:hidden safe-area-bottom" aria-label="Quick navigation">
        {bottomNav.map(item => (
          <Link
            key={item.href}
            to={item.href}
            aria-current={item.current ? 'page' : undefined}
            className={`flex flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] xs:text-[11px] font-medium ${item.current ? 'text-blue-600' : 'text-gray-500'}`}
          >
            <item.icon className="h-6 w-6" aria-hidden="true" />
            <span className="truncate max-w-full px-1">{item.name === 'Work From Home' ? 'WFH' : item.name}</span>
          </Link>
        ))}
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="flex flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] xs:text-[11px] font-medium text-gray-500"
          aria-label="More sections"
        >
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          <span>More</span>
        </button>
      </nav>

      <UserProfileModal
        isOpen={showUserProfileModal}
        onClose={() => setShowUserProfileModal(false)}
        userId={user?.userid}
      />

      <PasswordResetModal
        isOpen={showPasswordResetModal}
        onClose={hidePasswordResetModal}
        onSuccess={handlePasswordResetSuccess}
      />

      <AnnouncementModal
        isOpen={showAnnouncementModal}
        onClose={() => setShowAnnouncementModal(false)}
      />
    </div>
  );
};

export default Layout;
